import React, { useMemo } from "react";
import TagAndText from "./Tags/TagAndText";
import { Col, Flex, Row, Typography } from "antd";
import { formatNumber, formatTimeSpan } from "src/utils/formatting";
import dayjs from "dayjs";
import OverCurrentLimitTag from "./Tags/OverCurrentLimitTag";
import PremierPlusBinDTO from "src/models/PremierPlusBinDTO";
import GrainTempLimitTag from "./Tags/GrainTempLimitTag";
import FanOperationType from "src/consts/FanOperationType";
import CustomButton from "./CustomButton";
import { LeftOutlined, PauseOutlined, PlayCircleOutlined, StopOutlined } from "@ant-design/icons";
import FanStateTable from "./FanStateTable";
import { convertFahrenheitToCelsius } from "src/utils/generalUtils";

const { Title, Text } = Typography

interface FanRuntimeTagProps{
    fanRuntimeSeconds: number | null | undefined;
}
const FanRunTimeTag : React.FC<FanRuntimeTagProps> = ({ fanRuntimeSeconds }) => {

    if(fanRuntimeSeconds == null){
        return null;
    }

    const fanRuntimeDuration = dayjs.duration(fanRuntimeSeconds * 1_000);

    if(fanRuntimeSeconds < 60){
        return(
            <TagAndText items={[{ label: 'Fans have been running for: ', tag: `${formatNumber(fanRuntimeSeconds, { decimalPlaces: 1 })} seconds` }]}/>
        )
    }

    if(fanRuntimeSeconds > 60){
        return(
            <TagAndText items={[{ label: 'Fans have been running for: ', tag: `${fanRuntimeDuration.minutes()} minutes and ${fanRuntimeDuration.seconds()} seconds` }]}/>
        )
    }

    if(fanRuntimeSeconds >= 3600){
        return(
            <TagAndText items={[{ label: 'Fans have been running for: ', tag: `${Math.floor(fanRuntimeDuration.asHours())} hours, ${fanRuntimeDuration.minutes()} minutes, and ${fanRuntimeDuration.seconds()} seconds` }]}/>
        )
    }

    return null;
}

interface FanShutdownTimeTagProps {
    fanShutdownTime : string | null | undefined;
}

const FanShutdownTimeTag : React.FC<FanShutdownTimeTagProps> = ({ fanShutdownTime }) => {

    return(
        <TagAndText items={[{ label: 'Fans will shutdown at:', tag: `${dayjs(fanShutdownTime).tz("America/Chicago").format('MM/DD/YYYY HH:mm:ss')}` }]}/>
    )
}

interface RunFansViewProps {
    binDto: PremierPlusBinDTO | null | undefined;
}

const RunFansView : React.FC<RunFansViewProps> = ({ binDto }) => {

    const useCelsius = useMemo ( () => {
        return binDto?.platformSettings?.useMetricSystem;
    }, [binDto]);

    return (        
    <>
        <div style={{ display: "flex", flexDirection: "column" }}>
            <Flex vertical justify="center" style={{ padding:'20px' }}>
                <Row justify={'start'}>
                    <Col>
                        <Title level={3}>Run Fans</Title>
                    </Col>
                </Row>
                <Row gutter={[,16]} wrap>
                    <OverCurrentLimitTag binDto={binDto}/>
                    <GrainTempLimitTag binDto={binDto}/>
                </Row>
                <br/>
                <Row style={{ position: 'relative', border: '2px solid black', padding: '10px' }}>
                    <Col>
                        {
                            binDto?.fanRunOperation?.fanOperationType != FanOperationType.Off  && 
                            <>
                                {
                                    binDto?.fanRunOperation?.useTimer && 
                                        <div style={{ position: 'absolute', top: '10px', right: '10px' }}>
                                        {
                                            binDto?.fanRunOperation?.isFanTimerPaused ? 
                                            <CustomButton icon={<PlayCircleOutlined />} label={'UNPAUSE TIMER'}/>
                                            :
                                            <CustomButton icon={<PauseOutlined />} label={'PAUSE TIMER'}/>
                                        }
                                        </div>
                                }


                                {
                                    binDto?.fanRunOperation?.useTimer && 
                                    <FanShutdownTimeTag fanShutdownTime={binDto?.fanRunOperation?.fanShutOffTime} />
                                }
                                    
                                {
                                    binDto?.fanRunOperation?.useTimer && 
                                    <TagAndText 
                                    items={[{ 
                                        label: 'Total time ran: ', 
                                        tag: `${formatTimeSpan(binDto?.fanRunOperation?.timeElapsedSeconds)}`}]} 
                                    />
                                }
                                          
                                {
                                    (binDto?.fanRunOperation?.useEmcRange || binDto?.fanRunOperation?.useTempRange) && 
                                    <Row align={'middle'} gutter={24} style={{ padding:'4px' }}>
                                        <Col>
                                            <p style={{ color: '#000000', fontWeight: 'bold'}}>
                                                Fans will run between:
                                            </p>
                                        </Col>

                                        {
                                            binDto?.fanRunOperation?.useEmcRange &&
                                            <Col>
                                                <div style={{
                                                    paddingLeft: '8px',
                                                    paddingRight: '8px',
                                                    border: '2px solid black',
                                                    fontFamily: 'monospace',
                                                    borderRadius: '36px',
                                                }}>
                                                    <p style={{ color: '#000000', fontWeight: 'bold' }}>
                                                        {binDto?.fanRunOperation?.minWeatherMc} - {binDto?.fanRunOperation?.maxWeatherMc}% EMC
                                                    </p>
                                                </div>
                                            </Col>
                                        }

                                        {
                                            binDto?.fanRunOperation?.useEmcRange && binDto?.fanRunOperation?.useTempRange && 
                                            <p style={{ color: '#000000', fontWeight: 'bold' }}>and</p>
                                        }

                                        {
                                            binDto?.fanRunOperation?.useTempRange &&
                                            <Col>
                                                <div style={{
                                                    paddingLeft: '8px',
                                                    paddingRight: '8px',
                                                    border: '2px solid black',
                                                    fontFamily: 'monospace',
                                                    borderRadius: '36px',
                                                }}>
                                                    <p style={{ color: '#000000', fontWeight: 'bold'}}>
                                                        {
                                                            `${
                                                                useCelsius ? convertFahrenheitToCelsius(binDto?.fanRunOperation?.minWeatherTemperatureF) : binDto?.fanRunOperation?.minWeatherTemperatureF} - ${useCelsius ? convertFahrenheitToCelsius(binDto?.fanRunOperation?.maxWeatherTemperatureF) : binDto?.fanRunOperation?.maxWeatherTemperatureF}${useCelsius ? '°C': '°F'}.`
                                                        } 
                                                    </p>
                                                </div>
                                            </Col>
                                        }
                                    </Row>
                                }
                            </>
                        }  

                        <FanStateTable data={binDto?.fans ?? []}/>

                        <br/>

                        <Row justify={'end'} gutter={24}>
                            <Col>
                                <CustomButton 
                                disabled={true}
                                label={binDto?.fans?.some(f => f.isOn) ? 'START FANS' : 'CHANGE SETTINGS'}
                                />
                            </Col>
                            {
                                binDto?.fans?.some(f => f.isOn)  && 
                                <Col>
                                    <CustomButton 
                                    variant={'danger'} 
                                    icon={<StopOutlined />} 
                                    label={'STOP ALL FANS'} 
                                    disabled={true}
                                    />
                                </Col>
                            }
                        </Row>
                    </Col>
                </Row>
            </Flex>

            <div 
            style={{
                display: 'flex', 
                justifyContent: 'space-between',
                marginTop: "auto",
                padding: 12,
            }}> 
                <Col span={6}>
                    <CustomButton 
                    disabled={true} 
                    label={'Back'} 
                    variant='primary' 
                    icon={<LeftOutlined />}  
                    />
                </Col>
            </div>    
        </div>
    </>
    )
}

export default RunFansView;